import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';

import { fetchDataFromAPI } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService';
import {
  formatUtcDateKeyLabel,
  getUtcDateKeyFromApiValue,
} from 'utils/dateUtils';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';

const EXECUTION_LOG_ENDPOINT =
  'api/v2/nurturing-candidate-picker-execution-log/datagrid';
const FILTER_TRAY_PAGE_SIZE = 500;
const MAIQ_LEAD_STATUS_FIELD = 'maiq_lead_status';
const CREATED_AT_FIELD = 'created_at';

export type ExecutionLogFilterModel = {
  items: Array<{
    id?: number;
    columnField: string;
    operatorValue: string;
    value?: unknown;
  }>;
  linkOperator?: 'and' | 'or';
  orGroups?: Array<{
    items: Array<{
      id?: number;
      columnField: string;
      operatorValue: string;
      value?: unknown;
    }>;
  }>;
};

function toStr(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return String(value);
}

function formatMaiqLeadStatusLabel(value: string): string {
  return value
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function deriveMaiqLeadStatusValues(rows: unknown[]): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const status = toStr((row as Record<string, unknown>)?.maiq_lead_status).trim();
    if (status) values.add(status);
  }
  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

function buildMaiqLeadStatusFilterModel(
  selectedStatuses: string[] | undefined,
): ExecutionLogFilterModel | undefined {
  if (!Array.isArray(selectedStatuses) || selectedStatuses.length === 0) {
    return undefined;
  }
  if (selectedStatuses.length === 1) {
    return {
      items: [
        {
          id: 1,
          columnField: MAIQ_LEAD_STATUS_FIELD,
          operatorValue: 'equals',
          value: selectedStatuses[0],
        },
      ],
      linkOperator: 'and',
    };
  }
  return {
    items: [
      {
        id: 1,
        columnField: MAIQ_LEAD_STATUS_FIELD,
        operatorValue: 'isAnyOf',
        value: selectedStatuses,
      },
    ],
    linkOperator: 'and',
  };
}

function deriveCreatedAtDateKeys(rows: unknown[]): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const raw = (row as Record<string, unknown>)?.created_at;
    const dateKey = getUtcDateKeyFromApiValue(
      raw instanceof Date || typeof raw === 'string' ? raw : toStr(raw),
    );
    if (dateKey) values.add(dateKey);
  }
  return Array.from(values).sort((a, b) => b.localeCompare(a));
}

function buildUtcDayRangeItems(dateKey: string, idOffset: number) {
  const start = moment.utc(dateKey, 'YYYY-MM-DD', true).startOf('day');
  const end = start.clone().add(1, 'day');
  return [
    {
      id: idOffset,
      columnField: CREATED_AT_FIELD,
      operatorValue: 'onOrAfter',
      value: start.toISOString(),
    },
    {
      id: idOffset + 1,
      columnField: CREATED_AT_FIELD,
      operatorValue: 'before',
      value: end.toISOString(),
    },
  ];
}

function buildCreatedAtFilterModel(
  selectedDateKeys: string[] | undefined,
): ExecutionLogFilterModel | undefined {
  if (!Array.isArray(selectedDateKeys) || selectedDateKeys.length === 0) {
    return undefined;
  }

  if (selectedDateKeys.length === 1) {
    return {
      items: buildUtcDayRangeItems(selectedDateKeys[0], 1),
      linkOperator: 'and',
    };
  }

  const dateItems = selectedDateKeys.map((dateKey, index) => ({
    id: index + 1,
    columnField: CREATED_AT_FIELD,
    operatorValue: 'equals',
    value: moment.utc(dateKey, 'YYYY-MM-DD', true).startOf('day').toISOString(),
  }));

  return {
    items: dateItems,
    linkOperator: 'or',
  };
}

function buildCombinedExecutionLogFilterModel(
  statusModel?: ExecutionLogFilterModel,
  createdAtModel?: ExecutionLogFilterModel,
): ExecutionLogFilterModel | undefined {
  const statusItems = statusModel?.items ?? [];
  const dateItems = createdAtModel?.items ?? [];

  if (!statusItems.length && !dateItems.length) {
    return undefined;
  }

  if (statusItems.length && !dateItems.length) {
    return statusModel;
  }

  if (!statusItems.length && dateItems.length) {
    return createdAtModel;
  }

  const dateUsesOr =
    createdAtModel?.linkOperator === 'or' && dateItems.length > 1;

  if (dateUsesOr) {
    return {
      items: statusItems.map((item, index) => ({ ...item, id: index + 1 })),
      linkOperator: 'and',
      orGroups: [{ items: dateItems }],
    };
  }

  return {
    items: [...statusItems, ...dateItems].map((item, index) => ({
      ...item,
      id: index + 1,
    })),
    linkOperator: 'and',
  };
}

export interface UseNurturingCandidatePickerExecutionLogFilterTrayArgs {
  dealerId: string;
  runId?: string;
  /** Set true when the Quick Filters panel is open (prefetch for option values). */
  fetchEnabled?: boolean;
}

export interface UseNurturingCandidatePickerExecutionLogFilterTrayResult {
  categories: FilterTrayCategory[];
  optionsForActiveCategory: FilterTrayOption[];
  selectedByCategory: FilterTraySelection;
  onSelectionChange: (categoryId: string, selectedValues: string[]) => void;
  onClearAllFilters: () => void;
  onClearActiveCategory: () => void;
  onClearAllInPanel: () => void;
  executionLogFilterModel: ExecutionLogFilterModel | undefined;
  totalActiveCount: number;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
}

export function useNurturingCandidatePickerExecutionLogFilterTray({
  dealerId,
  runId,
  fetchEnabled = false,
}: UseNurturingCandidatePickerExecutionLogFilterTrayArgs): UseNurturingCandidatePickerExecutionLogFilterTrayResult {
  const [selectedByCategory, setSelectedByCategory] = useState<FilterTraySelection>(
    {},
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const selectedStatuses = selectedByCategory[MAIQ_LEAD_STATUS_FIELD];
  const selectedCreatedAtDates = selectedByCategory[CREATED_AT_FIELD];

  const maiqLeadStatusFilterModel = useMemo(
    () => buildMaiqLeadStatusFilterModel(selectedStatuses),
    [selectedStatuses],
  );

  const createdAtFilterModel = useMemo(
    () => buildCreatedAtFilterModel(selectedCreatedAtDates),
    [selectedCreatedAtDates],
  );

  const executionLogFilterModel = useMemo(
    () =>
      buildCombinedExecutionLogFilterModel(
        maiqLeadStatusFilterModel,
        createdAtFilterModel,
      ),
    [maiqLeadStatusFilterModel, createdAtFilterModel],
  );

  const params = useMemo(
    () => ({
      dealer: dealerId,
      limit: FILTER_TRAY_PAGE_SIZE,
      ...(runId ? { run_id: runId } : {}),
    }),
    [dealerId, runId],
  );

  const filtersForFetch = useMemo(() => {
    const hasFilterItems = (executionLogFilterModel?.items?.length ?? 0) > 0;
    const hasOrGroups = (executionLogFilterModel?.orGroups?.length ?? 0) > 0;
    return hasFilterItems || hasOrGroups
      ? JSON.stringify(executionLogFilterModel)
      : '';
  }, [executionLogFilterModel]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [
      'nurturing-candidate-picker-execution-log-filter-tray',
      dealerId,
      params,
      filtersForFetch,
    ],
    queryFn: () =>
      fetchDataFromAPI(
        EXECUTION_LOG_ENDPOINT,
        params,
        1,
        FILTER_TRAY_PAGE_SIZE,
        filtersForFetch,
      ),
    enabled: Boolean(dealerId) && fetchEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const statusOptionsFromData = useMemo(() => {
    const rows = data?.body?.data ?? [];
    return deriveMaiqLeadStatusValues(rows);
  }, [data]);

  const createdAtOptionsFromData = useMemo(() => {
    const rows = data?.body?.data ?? [];
    return deriveCreatedAtDateKeys(rows);
  }, [data]);

  const categories: FilterTrayCategory[] = useMemo(() => {
    const next: FilterTrayCategory[] = [];

    if (
      statusOptionsFromData.length > 0 ||
      (selectedStatuses?.length ?? 0) > 0
    ) {
      next.push({
        id: MAIQ_LEAD_STATUS_FIELD,
        label: 'MAIQ Lead Status',
        selectedCount: selectedStatuses?.length ?? 0,
      });
    }

    if (
      createdAtOptionsFromData.length > 0 ||
      (selectedCreatedAtDates?.length ?? 0) > 0
    ) {
      next.push({
        id: CREATED_AT_FIELD,
        label: 'Created At',
        selectedCount: selectedCreatedAtDates?.length ?? 0,
      });
    }

    return next;
  }, [
    selectedStatuses,
    selectedCreatedAtDates,
    statusOptionsFromData.length,
    createdAtOptionsFromData.length,
  ]);

  const optionsForActiveCategory: FilterTrayOption[] = useMemo(() => {
    if (activeCategoryId === MAIQ_LEAD_STATUS_FIELD) {
      const applied = selectedStatuses ?? [];
      const combined = Array.from(
        new Set([...statusOptionsFromData, ...applied]),
      ).sort((a, b) => a.localeCompare(b));
      return combined.map(value => ({
        value,
        label: formatMaiqLeadStatusLabel(value),
      }));
    }

    if (activeCategoryId === CREATED_AT_FIELD) {
      const applied = selectedCreatedAtDates ?? [];
      const combined = Array.from(
        new Set([...createdAtOptionsFromData, ...applied]),
      ).sort((a, b) => b.localeCompare(a));
      return combined.map(value => ({
        value,
        label: formatUtcDateKeyLabel(value),
      }));
    }

    return [];
  }, [
    activeCategoryId,
    statusOptionsFromData,
    selectedStatuses,
    createdAtOptionsFromData,
    selectedCreatedAtDates,
  ]);

  const onSelectionChange = useCallback(
    (categoryId: string, selectedValues: string[]) => {
      setSelectedByCategory(previous => ({
        ...previous,
        [categoryId]: selectedValues,
      }));
    },
    [],
  );

  const onClearAllFilters = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const onClearActiveCategory = useCallback(() => {
    if (!activeCategoryId) return;
    setSelectedByCategory(previous => ({
      ...previous,
      [activeCategoryId]: [],
    }));
  }, [activeCategoryId]);

  const onClearAllInPanel = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const totalActiveCount = useMemo(() => {
    const statusCount = selectedStatuses?.length ?? 0;
    const createdAtCount = selectedCreatedAtDates?.length ?? 0;
    return statusCount + createdAtCount;
  }, [selectedStatuses, selectedCreatedAtDates]);

  return {
    categories,
    optionsForActiveCategory,
    selectedByCategory,
    onSelectionChange,
    onClearAllFilters,
    onClearActiveCategory,
    onClearAllInPanel,
    executionLogFilterModel,
    totalActiveCount,
    activeCategoryId,
    setActiveCategoryId,
    isLoading: !!isLoading || !!isFetching,
    error: error as Error | null,
  };
}
